import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import {
  ownerModuleOfferApi,
  canonicalModuleOfferLocator, sameModuleOfferLocator,
  type ModuleOfferPageParams,
} from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import type {
  CommercialAcceptRequest,
  CommercialCommitmentOwnerPage,
  CommercialCommitmentOwnerView,
  CommercialOfferOwnerPage,
  CommercialOfferOwnerView,
  CommercialWorkspaceReadiness,
} from "@bw/shared/types/generated";
import { useEffect, useRef } from "react";

interface AcceptanceLease {
  actorId: string;
  orgId: string;
  versionId: string;
  token: symbol;
  request: CommercialAcceptRequest;
}

/** Local review effects only; this is not backend acceptance authority. */
export interface ModuleOfferAcceptanceEffects {
  revision: number;
  suspended: boolean;
}

/** Actor/org-keyed server facts; no grant or replacement acceptance. */
export function useModuleOffers(
  orgId: string | undefined,
  page?: ModuleOfferPageParams,
): UseQueryResult<CommercialOfferOwnerPage, Error> {
  const { user, loading } = useAuth();
  const org = canonicalModuleOfferLocator(orgId);
  return useQuery({
    queryKey: ["module-offers", user?.uid, org, page?.limit, page?.offset],
    queryFn: () => ownerModuleOfferApi.listOffers(org!, page),
    enabled: !loading && Boolean(user?.uid && org),
    retry: false,
  });
}
export function useModuleOffer(
  orgId: string | undefined,
  versionId: string | undefined,
): UseQueryResult<CommercialOfferOwnerView, Error> {
  const { user, loading } = useAuth();
  const org = canonicalModuleOfferLocator(orgId), version = canonicalModuleOfferLocator(versionId);
  return useQuery({
    queryKey: ["module-offer", user?.uid, org, version],
    queryFn: () => ownerModuleOfferApi.getOffer(org!, version!),
    enabled: !loading && Boolean(user?.uid && org && version),
    retry: false,
  });
}
export function useAcceptModuleOffer(
  orgId: string | undefined,
  versionId: string | undefined,
  effects: ModuleOfferAcceptanceEffects,
): UseMutationResult<
  CommercialCommitmentOwnerView,
  Error,
  CommercialAcceptRequest,
  AcceptanceLease
> {
  const { user } = useAuth();
  const client = useQueryClient();
  const org = canonicalModuleOfferLocator(orgId), version = canonicalModuleOfferLocator(versionId);
  const live = useRef(true);
  const current = useRef({ actorId: user?.uid, orgId: org, versionId: version, ...effects, token: Symbol() });
  if (current.current.actorId !== user?.uid || current.current.orgId !== org || current.current.versionId !== version
    || current.current.revision !== effects.revision || current.current.suspended !== effects.suspended) {
    current.current = { actorId: user?.uid, orgId: org, versionId: version, ...effects, token: Symbol() };
  }
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  return useMutation<CommercialCommitmentOwnerView, Error, CommercialAcceptRequest, AcceptanceLease>({
    mutationKey: ["module-offer-acceptance", user?.uid, org, version],
    onMutate: (request) => {
      const scope = current.current;
      if (!live.current || scope.suspended || !scope.actorId || !scope.orgId || !scope.versionId || !sameModuleOfferLocator(request.offer_version_id, scope.versionId)) {
        throw new Error("The current offer scope is unavailable.");
      }
      return { actorId: scope.actorId, orgId: scope.orgId, versionId: scope.versionId,
        token: scope.token, request: Object.freeze({ ...request }) };
    },
    mutationFn: (data: CommercialAcceptRequest) => {
      if (!live.current || current.current.suspended || !org || !sameModuleOfferLocator(data.offer_version_id, version)) throw new Error("The current offer scope is unavailable.");
      return ownerModuleOfferApi.accept(org, data);
    },
    retry: false,
    onSuccess: async (result, request, lease) => {
      const scope = current.current;
      if (!lease || !live.current || scope.suspended || lease.token !== scope.token || lease.actorId !== scope.actorId
        || lease.orgId !== scope.orgId || lease.versionId !== scope.versionId || !sameModuleOfferLocator(result.org_id, lease.orgId)
        || !sameModuleOfferLocator(result.offer.offer_version_id, lease.versionId) || request.offer_version_id !== lease.request.offer_version_id
        || request.expected_document_sha256 !== lease.request.expected_document_sha256
        || request.expected_owner_terms_fingerprint !== lease.request.expected_owner_terms_fingerprint
        || request.idempotency_key !== lease.request.idempotency_key) return;
      await Promise.all([
        client.invalidateQueries({
          queryKey: ["module-offers", lease.actorId, lease.orgId],
        }),
        client.invalidateQueries({
          queryKey: ["module-commitments", lease.actorId, lease.orgId],
        }),
        client.invalidateQueries({
          queryKey: ["commercial-readiness", lease.actorId, lease.orgId],
        }),
      ]);
    },
  });
}
export function useModuleCommitments(
  orgId: string | undefined,
  page?: ModuleOfferPageParams,
): UseQueryResult<CommercialCommitmentOwnerPage, Error> {
  const { user, loading } = useAuth();
  const org = canonicalModuleOfferLocator(orgId);
  return useQuery({
    queryKey: [
      "module-commitments",
      user?.uid,
      org,
      page?.limit,
      page?.offset,
    ],
    queryFn: () => ownerModuleOfferApi.listCommitments(org!, page),
    enabled: !loading && Boolean(user?.uid && org),
    retry: false,
  });
}
export function useModuleCommitment(
  orgId: string | undefined,
  acceptanceId: string | undefined,
): UseQueryResult<CommercialCommitmentOwnerView, Error> {
  const { user, loading } = useAuth();
  const org = canonicalModuleOfferLocator(orgId), acceptance = canonicalModuleOfferLocator(acceptanceId);
  return useQuery({
    queryKey: ["module-commitment", user?.uid, org, acceptance],
    queryFn: () => ownerModuleOfferApi.getCommitment(org!, acceptance!),
    enabled: !loading && Boolean(user?.uid && org && acceptance),
    retry: false,
  });
}
export function useCommercialReadiness(
  orgId: string | undefined,
): UseQueryResult<CommercialWorkspaceReadiness, Error> {
  const { user, loading } = useAuth();
  const org = canonicalModuleOfferLocator(orgId);
  return useQuery({
    queryKey: ["commercial-readiness", user?.uid, org],
    queryFn: () => ownerModuleOfferApi.getReadiness(org!),
    enabled: !loading && Boolean(user?.uid && org),
    retry: false,
  });
}
